import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BsCheck } from 'react-icons/bs';
import { HiOutlinePencil, HiCheck, HiX } from 'react-icons/hi';
import { updateTask } from '../../../actions/task';
import { InputText, Textarea } from '../../../lib/HooksFormFields';
import ModalStandalone from '../../../lib/ModalStandalone';
import Loader from '../../Loader';
import load from '../../../assets/images/task-types/load.svg';
import unload from '../../../assets/images/task-types/unload.svg';
import prepare from '../../../assets/images/task-types/prepare.svg';
import review from '../../../assets/images/task-types/review.svg';
import styles from './task-articles.module.scss';
import { hasAccess, canEditArticleQuantities } from '../../../utils';
import {
  getDisplayableArticles,
  getOkQuantitySurplus,
  getTotalHsQuantity,
  getArticleQuantityByType,
  applyArticleQuantityUpdate,
  ARTICLE_QUANTITY_TYPE_LABELS,
} from '../../../utils/taskArticles';

const TaskArticles = ({
  taskId,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { taskReducer, authReducer } = useSelector((store) => store);
  const { user } = authReducer;
  const { tasks, changedAt, isLoading } = taskReducer;
  const [task, setTask] = useState({});
  const {
    control,
    setValue,
    watch,
    handleSubmit,
  } = useForm({
    defaultValues: {
      numberRetour: '',
      purchaseOrder: '',
    },
  });

  // const watchNumberRetour = watch('numberRetour');
  const watchPurchaseOrder = watch('purchaseOrder');
  const [isEditingComment, setIsEditingComment] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [selectedNonLvtecFile, setSelectedNonLvtecFile] = useState(null);
  const [editingQuantity, setEditingQuantity] = useState(null);
  const [quantityValue, setQuantityValue] = useState('');
  const modalNonLvtecRef = useRef();
  const modalQuantityRef = useRef();

  useEffect(() => {
    const found = (tasks || []).find((t) => t._id === taskId);
    if (found) {
      setTask(found);
      if (found?.type === 'Retour') {
        setValue('numberRetour', found?.numberRetour || '');
      }
      if (found?.type === 'Préparation') {
        setValue('purchaseOrder', found?.purchaseOrder || '');
      }
    }
  }, [tasks, taskId, changedAt]);

  const toggleCheckbox = async (articleId, isNonLvtec = false) => {
    if (isNonLvtec) {
      await updateTask(dispatch, {
        ...task,
        articlesNonLvtec: (task.articlesNonLvtec || []).map((article) => {
          const checkedSage = article._id === articleId
            ? !article.checkedSage
            : article.checkedSage;
          return { ...article, checkedSage };
        }),
      });
      return;
    }
    await updateTask(dispatch, {
      ...task,
      articles: task.articles.map((article) => {
        const checkedSage = article._id === articleId ? !article.checkedSage : article.checkedSage;
        return { ...article, checkedSage };
      }),
    });
  };

  const submitNumber = async (data) => {
    const dataNumber = {};
    if (task?.type === 'Retour') {
      dataNumber.numberRetour = data.numberRetour;
    }
    if (task?.type === 'Préparation') {
      dataNumber.purchaseOrder = data.purchaseOrder;
    }
    await updateTask(dispatch, {
      _id: task._id,
      ...dataNumber,
    });
  };

  const handleSurplusChange = async (checked) => {
    await updateTask(dispatch, {
      _id: task._id,
      surplusToManage: checked,
    });
  };

  const handleSaveComment = async (data) => {
    if (!editingArticleId) return;

    await updateTask(dispatch, {
      ...task,
      articles: task.articles.map((article) => {
        if (article._id === editingArticleId) {
          return { ...article, comment: data.comment };
        }
        return article;
      }),
    }).then(() => {
      setIsEditingComment(false);
      setEditingArticleId(null);
    });
  };

  const startEditingComment = (articleId, isNonLvtec = false) => {
    if (isNonLvtec) return;
    setEditingArticleId(articleId);
    setIsEditingComment(true);
    const article = task.articles.find((a) => a._id === articleId);
    setValue('comment', article?.comment || '');
  };

  const cancelEditingComment = () => {
    setIsEditingComment(false);
    setEditingArticleId(null);
  };

  const openQuantityEdit = (article, type) => {
    if (!canEditArticleQuantities(user?.role) || article?.isNonLvtec) return;
    const currentValue = getArticleQuantityByType(article, type);
    setEditingQuantity({
      articleId: article._id,
      articleLabel: article.label,
      type,
    });
    setQuantityValue(String(currentValue));
    modalQuantityRef?.current?.open?.();
  };

  const closeQuantityEdit = () => {
    setEditingQuantity(null);
    setQuantityValue('');
    modalQuantityRef?.current?.close?.();
  };

  const handleSaveQuantity = async () => {
    if (!editingQuantity) return;

    await updateTask(dispatch, {
      ...task,
      articles: task.articles.map((article) => {
        if (article._id !== editingQuantity.articleId) return article;
        return applyArticleQuantityUpdate(article, editingQuantity.type, quantityValue);
      }),
    }).then(() => {
      closeQuantityEdit();
    });
  };

  const renderQuantityBadge = (type, value, article) => {
    if (!value) return null;

    const badge = (
      <span className={`${styles.icon} ${styles[type]}`}>
        <span>{value}</span>
      </span>
    );

    if (!canEditArticleQuantities(user?.role) || article?.isNonLvtec) {
      return badge;
    }

    return (
      <button
        type="button"
        className={styles.quantityBadgeButton}
        onClick={() => openQuantityEdit(article, type)}
        title={`Modifier la quantité ${ARTICLE_QUANTITY_TYPE_LABELS[type]}`}
      >
        {badge}
      </button>
    );
  };

  const arrArticles = useMemo(() => getDisplayableArticles(task), [task]);

  const isTaskLoading = useMemo(() => (
    isLoading.some((d) => /^TASK_PUT+.*/.test(d))
  ), [isLoading]);

  const nonLvtecImgUrl = useMemo(() => {
    if (!selectedNonLvtecFile?.path) return null;
    const token = localStorage.getItem('token');
    return `${process.env.REACT_APP_API_URL}/static/${selectedNonLvtecFile.path.replace('upload/', '')}?token=${token}`;
  }, [selectedNonLvtecFile]);

  const renderArticleComment = (article) => {
    if (article?.isNonLvtec) return null;

    const isEditing = isEditingComment && editingArticleId === article._id;

    if (isEditing) {
      return (
        <form
          onSubmit={handleSubmit(handleSaveComment)}
          className={styles.commentForm}
        >
          <Textarea
            name="comment"
            control={control}
            placeholder="Ajouter un commentaire..."
            label=""
          />
          <div className={styles.commentActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={cancelEditingComment}
            >
              <HiX size="16px" />
              Annuler
            </button>
            <button
              type="submit"
              className={styles.saveButton}
            >
              <HiCheck size="16px" />
              Sauvegarder
            </button>
          </div>
        </form>
      );
    }

    return (
      <div className={styles.commentSection}>
        {hasAccess(user.role) && (
          <button
            className={styles.editCommentButton}
            onClick={() => startEditingComment(article._id)}
            title="Modifier le commentaire"
          >
            <HiOutlinePencil size="16px" />
          </button>
        )}
        {article.comment ? (
          <p className={styles.comment}>{article.comment}</p>
        ) : (
          <p className={styles.noComment}>Aucun commentaire</p>
        )}
      </div>
    );
  };

  return (
    <>
    <ModalStandalone
      handleClose={() => setSelectedNonLvtecFile(null)}
      ref={modalNonLvtecRef}
      zIndex={3000}
    >
      <div className={styles.modalImage}>
        {nonLvtecImgUrl && (
          <img
            src={nonLvtecImgUrl}
            alt={selectedNonLvtecFile?.name || 'image'}
          />
        )}
      </div>
    </ModalStandalone>
    <ModalStandalone
      handleClose={closeQuantityEdit}
      ref={modalQuantityRef}
      zIndex={3001}
      hideCloseButton
    >
      {editingQuantity && (
        <div className={styles.quantityModal}>
          <h3>
            {`Modifier la quantité ${ARTICLE_QUANTITY_TYPE_LABELS[editingQuantity.type]}`}
          </h3>
          <p className={styles.quantityModalArticle}>{editingQuantity.articleLabel}</p>
          <label htmlFor="article-quantity-input">Quantité</label>
          <input
            id="article-quantity-input"
            type="number"
            min={0}
            value={quantityValue}
            onChange={(e) => setQuantityValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveQuantity();
            }}
          />
          <div className={styles.quantityModalActions}>
            <button
              type="button"
              className={styles.quantityModalCancel}
              onClick={closeQuantityEdit}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.quantityModalSave}
              onClick={handleSaveQuantity}
            >
              Enregistrer
            </button>
          </div>
        </div>
      )}
    </ModalStandalone>
    <div className={styles.modalArticles}>
    <div className={styles.header}>
      {task?.type === 'Chargement' && <img src={load} alt='chargement' />}
      {task?.type === 'Déchargement' && <img src={unload} alt='déchargement' />}
      {task?.type === 'Préparation' && <img src={prepare} alt='préparation' />}
      {task?.type === 'Retour' && <img src={review} alt='retour' />}
      {(task?.case?.ref && task?.case?.name) && (
        <div className={styles.headerInfo}>
          <p className={styles.client}>
          {task?.case?.client} {task?.number && <>/ <span>{task?.number}</span></>}
          </p>
          <p>
            {(!task?.date && !task?.case?.name) && '---'}
            {task?.date && <>{format(new Date(task?.date), 'dd.MM.yy', { locale: fr })}</>}
            {task?.case?.name && <span> {task?.case?.name}</span>}
          </p>
          <p>{`#${task?.case?.ref}`}</p>
        </div>
      )}
      {task?.type === 'Retour' && hasAccess(user.role) && (
        <label className={styles.surplusCheckbox}>
          <input
            type="checkbox"
            checked={Boolean(task.surplusToManage)}
            onChange={(e) => handleSurplusChange(e.target.checked)}
          />
          <span>Surplus à gérer</span>
        </label>
      )}
    </div>
    <div>
      <div className={styles.baskets}>
        <div className={styles.basket}>
          <p><label>Paniers préparés</label> : {task?.basketsCount}</p>
        </div>
        <div className={styles.basket}>
          <p><label>Paniers chargés</label> : {task?.basketsCountLoaded}</p>
        </div>
        <div className={styles.basket}>
          <p><label>Paniers déchargés</label> : {task?.basketsCountUnloaded}</p>
        </div>
      </div>
      {hasAccess(user.role) && (
        <>
          {(task?.type === 'Préparation') && (
            <div className={styles.fieldNumber}>
              <InputText
                name='purchaseOrder'
                control={control}
                placeholder='N° du bon de livraison...'
              />
              <button
                onClick={handleSubmit(submitNumber)}
                disabled={!watchPurchaseOrder}
              >
                Enregistrer n° du bon de livraison
              </button>
            </div>
          )}
        </>
      )}
      <ul className={!isTaskLoading ? styles.articles : `${styles.articles} ${styles.loading}`}>
        {isTaskLoading && <Loader />}
        {arrArticles.map((article) => {
          const okSurplus = task?.type === 'Retour' ? getOkQuantitySurplus(article) : 0;

          return (
          <li key={`article-${article._id}`}>
            <div className={styles.content}>
              <div className={article?.substitutes?.length > 0 ? `${styles.article} ${styles.withSubtitutes}` : styles.article}>
                <p className={styles.ref}>{article.ref}</p>
                <div className={styles.infosArticle}>
                  <p className={styles.articleName}>
                    <span>{article.label}</span>
                    {article?.unit && <span>{article.unit}</span>}
                  </p>
                  {article.isNonLvtec && article?.file && typeof article.file === 'object' && (
                    <div>
                      <button
                        type="button"
                        className={styles.editCommentButton}
                        onClick={() => {
                          setSelectedNonLvtecFile(article.file);
                          modalNonLvtecRef?.current?.open?.();
                        }}
                      >
                        Voir l'image
                      </button>
                    </div>
                  )}
                  <div className={styles.quantity}>
                    {(() => {
                      const totalHs = getTotalHsQuantity(article);
                      return totalHs ? (
                        <div className={styles.contentQuantity}>
                          {renderQuantityBadge('hs', totalHs, article)}
                        </div>
                      ) : '';
                    })()}
                    {(() => {
                      const totalDamaged = getArticleQuantityByType(article, 'damaged');
                      return totalDamaged ? (
                        <div className={styles.contentQuantity}>
                          {renderQuantityBadge('damaged', totalDamaged, article)}
                        </div>
                      ) : '';
                    })()}
                    {article.return?.ok ? (
                      <div className={styles.contentQuantity}>
                        {renderQuantityBadge('ok', article.return.ok, article)}
                      </div>
                    ) : ''}
                    {task?.type === 'Retour' ? (
                      <div className={styles.plannedQuantity}>
                        <p>
                          <span>{article.quantity}</span>
                        </p>
                      </div>
                    ) : (
                      <div className={styles.plannedQuantity}>
                        <p>
                          <span>{article.checked ? article.quantity : 0}</span>
                          <>/ {article.plannedQuantity}</>
                        </p>
                      </div>
                    )}
                  </div>
                  {okSurplus > 0 && (
                    <p className={styles.okSurplusAlert}>
                      {` (+${okSurplus})`}
                    </p>
                  )}
                </div>
                {(article.return?.damagedComment
                  || article.return?.hsComment
                  || (Array.isArray(article.hs) && article.hs.length > 0)
                  || (Array.isArray(article.damaged) && article.damaged.length > 0)) && (
                  <div className={styles.comments}>
                    {Array.isArray(article.damaged)
                      && article.damaged.map((damagedItem, damagedIndex) => (
                        damagedItem?.comment ? (
                          <p
                            key={damagedItem._id || `damaged-${article._id}-${damagedIndex}`}
                            className={`${styles.comment} ${styles.damaged}`}
                          >
                            <span>
                              {damagedItem.quantity ? `${damagedItem.quantity} - ` : ''}
                              {damagedItem.comment}
                            </span>
                          </p>
                        ) : null
                      ))}
                    {article.return?.damagedComment && !(article.damaged?.length) && (
                      <p className={`${styles.comment} ${styles.damaged}`}>
                        <span>{article.return.damagedComment}</span>
                      </p>
                    )}
                    {Array.isArray(article.hs) && article.hs.map((hsItem, hsIndex) => (
                      hsItem?.comment ? (
                        <p
                          key={hsItem._id || `hs-${article._id}-${hsIndex}`}
                          className={`${styles.comment} ${styles.hs}`}
                        >
                          <span>
                            {hsItem.quantity ? `${hsItem.quantity} - ` : ''}
                            {hsItem.comment}
                          </span>
                        </p>
                      ) : null
                    ))}
                    {article.return?.hsComment && !(article.hs?.length) && (
                      <p className={`${styles.comment} ${styles.hs}`}>
                        <span>{article.return.hsComment}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
              {article?.substitutes?.length > 0 && (
                <>
                  <h3>
                    Produit{article?.substitutes.length > 1 && 's'} de substitution
                  </h3>
                  <div className={styles.substitutes}>
                  {article?.substitutes.map((substitute) => (
                    <div className={styles.substitute} key={`substitute-${substitute._id}`}>
                      <div className={styles.quantity}>
                        <p>
                          {substitute.quantity} -&nbsp;
                        </p>
                      </div>
                      <div>
                        <p className={styles.substituteName} >{substitute.label}</p>
                        {substitute.comment && (
                          <p className={styles.comment}>{substitute.comment}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
              {renderArticleComment(article)}
            </div>
            <Checkbox
              checked={article?.checkedSage}
              handleClick={() => toggleCheckbox(article._id, article.isNonLvtec)}
              isDisabled={!hasAccess(user.role)}
            />
          </li>
          );
        })}
      </ul>
    </div>
    <div className={styles.containerButton}>
      <button onClick={() => handleClose()}>Fermer le détail</button>
    </div>
  </div>
  </>
  );
};

const Checkbox = (({ checked, handleClick, isDisabled = false }) => {
  useEffect(() => {}, []);
  return (
    <div className={`${styles.checkbox} ${isDisabled ? styles.disabled : ''}`} onClick={() => {
      if (!isDisabled) handleClick();
    }}>
      {checked && <BsCheck />}
    </div>
  );
});

export default TaskArticles;
